import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import api from "../services/api";

const Roles = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [selectedPermissionIds, setSelectedPermissionIds] = useState(new Set());
  const [saving, setSaving] = useState(false);

  const allPermissions = (() => {
    const map = new Map();
    roles.forEach((role) => {
      (role.permissions || []).forEach((p) => map.set(p.id, p));
    });
    return Array.from(map.values());
  })();

  const loadRoles = async () => {
    setLoading(true);
    try {
      const data = await api.get("/api/v1/roles");
      setRoles(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error(error.message || "Failed to load roles");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRoles();
  }, []);

  const openEdit = (role) => {
    setEditingRole(role);
    setSelectedPermissionIds(new Set((role.permissions || []).map((p) => p.id)));
  };

  const togglePermission = (id) => {
    setSelectedPermissionIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/api/v1/roles/${editingRole.id}/permissions`, {
        permissionIds: Array.from(selectedPermissionIds),
      });
      toast.success("Role permissions updated");
      setEditingRole(null);
      loadRoles();
    } catch (error) {
      toast.error(error.message || "Failed to update permissions");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4" style={{ position: "relative" }}>
      {loading ? (
        <div className="loading-overlay">
          <div className="spinner-card">
            <div className="spinner-ring" />
            <div className="spinner-label">Loading roles...</div>
          </div>
        </div>
      ) : null}

      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>ID</th>
              <th>Role</th>
              <th>Description</th>
              <th>Permissions</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {roles.length === 0 ? (
              <tr>
                <td colSpan={5}>No roles found.</td>
              </tr>
            ) : (
              roles.map((role) => (
                <tr key={role.id}>
                  <td>{role.id}</td>
                  <td>
                    <span className="badge badge-blue">{role.name}</span>
                  </td>
                  <td>{role.description || "-"}</td>
                  <td>{(role.permissions || []).length} granted</td>
                  <td className="action-buttons">
                    <button className="secondary" onClick={() => openEdit(role)}>
                      Edit Permissions
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {editingRole ? (
        <div className="modal-overlay" role="presentation" onClick={(e) => e.target === e.currentTarget && setEditingRole(null)}>
          <div className="modal-box" style={{ maxWidth: "600px" }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold">Permissions — {editingRole.name}</h3>
              <button className="close-btn" onClick={() => setEditingRole(null)}>
                Close
              </button>
            </div>
            <div style={{ maxHeight: "50vh", overflowY: "auto" }}>
              {allPermissions.length === 0 ? (
                <div className="page-subtitle">No permissions available to assign.</div>
              ) : (
                allPermissions.map((permission) => (
                  <label
                    key={permission.id}
                    style={{ display: "flex", alignItems: "center", gap: "10px", padding: "8px 4px", borderBottom: "1px solid #eee" }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedPermissionIds.has(permission.id)}
                      onChange={() => togglePermission(permission.id)}
                    />
                    <span>
                      <strong>{permission.code}</strong>
                      {permission.description ? ` — ${permission.description}` : ""}
                    </span>
                  </label>
                ))
              )}
            </div>
            <div className="modal-actions">
              <button type="button" className="secondary" onClick={() => setEditingRole(null)}>
                Cancel
              </button>
              <button type="button" className="primary" onClick={handleSave} disabled={saving}>
                {saving ? "Saving..." : "Save Permissions"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Roles;
